import { useEffect } from 'react';
import { useEventDetails } from '../../hooks/useEventDetails';

interface CustomCSSProviderProps {
  customCss?: string;
  style?: {
    backgroundColor?: string;
    headerColor?: string;
  };
}

/**
 * CustomCSSProvider injects custom CSS from team.style.css, event.style.css, 
 * and event.detail_style_sheet into the document head.
 * 
 * This supports V1 events where planners can add custom CSS to style their event pages.
 * The CSS is injected in order of precedence: team → event → detail stylesheet
 */
export const CustomCSSProvider: React.FC<CustomCSSProviderProps> = ({ customCss, style }) => {
  // Extract event slug from URL path
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const eventSlug = pathParts[0]; // First part after domain is the event slug
  
  const { data: eventDetails } = useEventDetails(eventSlug);
  
  // Use API custom CSS if available, otherwise fall back to initial custom CSS
  const effectiveCss = eventDetails?.customCss || customCss;
  
  // Dynamic button color injection - runs before custom CSS
  useEffect(() => {
    if (!style?.headerColor) return;

    // Create style element for dynamic button colors
    const buttonStyleElement = document.createElement('style');
    buttonStyleElement.setAttribute('data-custom-css', 'true');
    buttonStyleElement.setAttribute('data-source', 'dynamic-button-colors');
    buttonStyleElement.textContent = `
      .cs-new-blue-btn,
      a.cs-new-blue-btn {
        background-color: ${style.headerColor} !important;
      }
      .cs-new-blue-btn:hover,
      a.cs-new-blue-btn:hover,
      .cs-new-blue-btn:focus,
      a.cs-new-blue-btn:focus {
        background-color: ${style.headerColor} !important;
        opacity: 0.85;
      }
      .cs-new-blue-btn:active,
      a.cs-new-blue-btn:active {
        background-color: ${style.headerColor} !important;
        opacity: 0.75;
      }
    `;
    
    // Append to head
    document.head.appendChild(buttonStyleElement);

    // Cleanup function
    return () => {
      if (buttonStyleElement.parentNode) {
        document.head.removeChild(buttonStyleElement);
      }
    };
  }, [style?.headerColor]);
  
  // Custom CSS injection - runs after dynamic button colors
  useEffect(() => {
    if (!effectiveCss || effectiveCss.trim() === '') return;

    // Create style element for custom CSS
    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-custom-css', 'true');
    styleElement.setAttribute('data-source', 'v1-event-custom-styles');
    styleElement.textContent = effectiveCss;
    
    // Append to head
    document.head.appendChild(styleElement);

    // Cleanup function to remove the style element when component unmounts
    return () => {
      if (styleElement.parentNode) {
        document.head.removeChild(styleElement);
      }
    };
  }, [effectiveCss]);

  return null;
};